import React, { useEffect, useMemo, useState } from "react";

import { Panel, Th, Eyebrow, MicroBadge, Lead, AlertGlyph } from "./Panel";
import CircularLoader from "../../CircularLoader/CircularLoader";
import NewNamedChargeModal from "./NewNamedChargeModal";
import ImagesModal from "../../../Pages/Shipper/Customer/MainPages/Orders/Components/OrderHistoryModal/ImagesModal/ImagesModal";
import { AttachmentChip, fileSize } from "./AttachmentChip";
import { fixedDecimals, convertTime } from "../../../helpers/helpers";
import {
  buildLineRows,
  fixedLinesFor,
  TRANSPORT_KEYS,
  previewDelta,
  submitAddCharge,
  submitModifyCharge,
  seedModifyTaxEdits,
  nextChargeId,
  markModifyResult,
} from "../writes/chargeModel";
import { readReferral } from "./referralDisplay";
import { MdAdd, MdEdit, MdClose, MdAttachFile } from "react-icons/md";
import { Trans, useTranslation } from "react-i18next";

/**
 * The charge ledger.
 *
 * The layout follows how the data actually works, which the column names hide:
 *
 *   BASE INVOICE   revision 1. Its components sit INSIDE sub_total, sub_total
 *                  is the quote's sub_total times the markup, and
 *                  sub_total + tax = that revision's total.
 *   ADJUSTMENTS    every later revision. Each is an addition that bypasses
 *                  markup and tax and lands on the total at face value —
 *                  except a Modify, which replaces the accumulated state and
 *                  counts only for what it changed (chargeModel.js).
 *   TOTAL          base total + every adjustment.
 *
 * Showing the latest revision's columns as "current amounts" is wrong — those
 * are the most recent delta only.
 */

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const money = (value) =>
  value == null || value === "" || Number(value) === 0
    ? "—"
    : fixedDecimals(value);

const signed = (value) => {
  const n = Number(value) || 0;
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${fixedDecimals(Math.abs(n))}`;
};

/* ── Table primitives ───────────────────────────────────────────────────
   These MUST stay at module scope. Declared inside ChargeLedger they were
   recreated on every render, so each keystroke handed React a new element
   type, the live <input> was unmounted and remounted, and focus was lost
   after the first character — an admin typing 125.50 committed "1". The
   payload builders were byte-perfect; they were simply being fed a truncated
   number. Anything this file adds later must be declared out here too.

   `ctx` carries the render flags these used to close over. */

const Input = ({ value, onChange, isAdd, name }) => (
  <input
    type="text"
    /* `name` mirrors production's inputs (BillingTable gives each cell the
       payload key as its name), so both designs are queryable the same way and
       a parity test can drive them identically. It is not submitted anywhere —
       these inputs are not inside a <form>. */
    name={name}
    aria-label={name}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={isAdd ? "0.00" : ""}
    className={`w-[84px] rounded border px-1.5 py-0.5 text-right font-manrope text-[11.5px] font-semibold tabular-nums text-ui-text outline-none ${
      isAdd
        ? "border-state-success-border bg-state-success-bg focus:border-state-success-solid"
        : "border-state-info-border bg-ledger-billed focus:border-brand"
    }`}
  />
);

const Row = ({
  ctx,
  label,
  hint,
  badge,
  badgeTone,
  quote,
  billed,
  tone,
  bold,
  editKey,
  edits,
  setEdits,
}) => {
  const { t } = useTranslation("admin");
  return (
  <tr className="border-b border-ui-border-subtle hover:bg-ui-row-hover">
    <td
      className={`px-[9px] py-1 font-manrope text-[11.5px] leading-[1.3] text-ui-text ${
        bold ? "font-bold" : "font-medium"
      }`}
    >
      {label}
      {badge && <MicroBadge tone={badgeTone}>{badge}</MicroBadge>}
      {hint && (
        <span className="ml-1.5 font-manrope text-[10px] font-medium text-ui-text-muted">
          {hint}
        </span>
      )}
    </td>
    {ctx.compare && (
      <td className="px-[9px] py-1 text-right font-manrope text-[11.5px] font-medium leading-[1.3] tabular-nums text-ui-text-secondary">
        {money(quote)}
      </td>
    )}
    <td
      className={`px-[9px] py-1 text-right font-manrope text-[11.5px] leading-[1.3] tabular-nums ${
        tone === "markup"
          ? "bg-ledger-service font-semibold text-state-purple-text"
          : "bg-ledger-billed text-ui-text"
      } ${bold ? "font-bold" : "font-semibold"}`}
    >
      {money(billed)}
    </td>
    {ctx.editing && (
      <td className="px-[9px] py-1 text-right">
        {/* `!= null`, not truthiness: production renders an input for a line
            named "" (BillingTable.jsx:244-252, 268-274) and its save filter
            finds it under the "" key, so such a line is editable and sent.
            A line with NO NameEn gets no input. In production its input has
            no name attribute, so typing there writes under the "" key: sent
            only if the shipment ALSO has a line named "" (then that line goes
            out with the typed amount). Not reproduced — it needs a malformed
            nameless line beside a ""-named one. See COVERAGE.md. */}
        {editKey != null ? (
          <Input
            isAdd={ctx.isAdd}
            name={editKey}
            value={edits[editKey]}
            onChange={(value) =>
              setEdits((prev) => ({ ...prev, [editKey]: value }))
            }
          />
        ) : (
          <span className="font-manrope text-[10px] text-ui-text-subtle">
            {t("invoice.server")}
          </span>
        )}
      </td>
    )}
  </tr>
);
};

/** A flat ledger line. Where part of the amount arrived after booking, the
 *  revision and its note sit under the label rather than in a separate block —
 *  same information, one reading order. */
const ChargeRow = ({ ctx, line }) => {
  const { t } = useTranslation("admin");
  // a Modify sets the line outright, so the latest one is the provenance that
  // explains the figure; otherwise the first addition, as before
  const modifyOrigin = [...(line.origins || [])].reverse().find((o) => o.modify);
  const origin = modifyOrigin || line.origins?.[0];
  const partial = line.origins?.length > 0 && line.baseValue > 0;
  return (
    <tr className="border-b border-ui-border-subtle hover:bg-ui-row-hover">
      <td className="px-[9px] py-1 font-manrope text-[11.5px] font-medium leading-[1.3] text-ui-text">
        {t(`invoice.lines.${line.key}`, { defaultValue: line.label })}
        {line.derived && <MicroBadge>{t("invoice.derived")}</MicroBadge>}
        {origin && (
          <div className="font-manrope text-[10px] font-medium text-ui-text-muted">
            {modifyOrigin
              ? t("invoice.setByModify", { rev: origin.revision })
              : partial
                ? t("invoice.includesAdded", { amount: fixedDecimals(line.added), rev: origin.revision })
                : t("invoice.addedAfterBooking", { rev: origin.revision })}
            {origin.description ? ` · ${origin.description}` : ""}
            {origin.createdAt ? ` · ${convertTime(origin.createdAt)}` : ""}
          </div>
        )}
        {line.derived && (
          <div className="font-manrope text-[10px] font-medium text-ui-text-muted">
            {line.note
              ? t(`invoice.lineNotes.${line.key}`, { defaultValue: line.note })
              : t("invoice.billedInsideSubtotal")}
          </div>
        )}
      </td>
      {ctx.compare && (
        <td className="px-[9px] py-1 text-right font-manrope text-[11.5px] font-medium leading-[1.3] tabular-nums text-ui-text-secondary">
          {money(line.quote)}
        </td>
      )}
      <td className="bg-ledger-billed px-[9px] py-1 text-right font-manrope text-[11.5px] font-semibold leading-[1.3] tabular-nums text-ui-text">
        {money(line.amount)}
      </td>
      {ctx.editing && <td className="px-[9px] py-1" />}
    </tr>
  );
};

const Section = ({ ctx, children, right }) => (
  <tr className="bg-ui-surface-sunken">
    <td colSpan={ctx.cols - (right ? 1 : 0)} className="px-[9px] py-[3px]">
      <Eyebrow>{children}</Eyebrow>
    </td>
    {right && (
      <td className="px-[9px] py-[3px] text-right">
        <Eyebrow>{right}</Eyebrow>
      </td>
    )}
  </tr>
);

const ChargeLedger = ({ data, setData, setShipments, setSnack }) => {
  const { t } = useTranslation("admin");
  const [mode, setMode] = useState("idle"); // idle | add | modify
  const [fixedEdits, setFixedEdits] = useState({});
  const [taxEdits, setTaxEdits] = useState({});
  const [additionalEdits, setAdditionalEdits] = useState({});
  // The note is NOT separate state: production keeps it in newChargeData, and
  // its key position and ""-vs-absent-vs-null value follow from that. It lives
  // in fixedEdits.description — see submitAddCharge / submitModifyCharge.
  const [files, setFiles] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showQuote, setShowQuote] = useState(true);
  const [namedModal, setNamedModal] = useState(null);
  const [extraTaxes, setExtraTaxes] = useState([]);
  const [extraAdditional, setExtraAdditional] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [fullview, setFullview] = useState();

  /* Object URLs for picked images so the chip can show the actual picture.
     They are revoked when the selection changes or the editor closes —
     otherwise every file picked in a session leaks until the tab is gone. */
  const pending = useMemo(
    () =>
      (files || []).map((file) => {
        const isImage = (file.type || "").startsWith("image/");
        return {
          file,
          isImage,
          url: isImage ? URL.createObjectURL(file) : null,
        };
      }),
    [files],
  );

  useEffect(
    () => () => pending.forEach((p) => p.url && URL.revokeObjectURL(p.url)),
    [pending],
  );

  const rows = useMemo(() => buildLineRows(data), [data]);
  const lines = useMemo(() => fixedLinesFor(data), [data]);

  // Display only (referralDisplay.js) — never read by a payload builder.
  const referral = readReferral(data);
  // Shipments charged only at payment get the referral markup added to Total
  // on top of the lines and taxes; show it as a line only then, so the column
  // still adds up. Otherwise it is already inside the billed charges.
  const referralOnTop =
    referral.amount > 0 &&
    Math.abs(
      rows.total - rows.flatSubtotal - rows.flatTaxTotal - referral.amount,
    ) <= 0.01;
  // When the billed lines and taxes already add up to Total, the markup (which
  // is part of Total) must be inside them. Say so only when that is proven.
  const referralInside =
    referral.amount > 0 &&
    !referralOnTop &&
    Math.abs(rows.total - rows.flatSubtotal - rows.flatTaxTotal) <= 0.01;

  /* Production's catalogue is rebuilt from `data` on EVERY setData
     (BillingAdmin.jsx:348-360), which throws away a line invented in the
     New Tax / Additional Charge modal while its amount stays in the edit bag.
     So a write from another panel mid-Add (status, note, …) makes that line
     vanish from the table and from the POST. Matches Classic
     BillingAdmin.jsx:348-360 + 188-199, see COVERAGE.md. Only the invented
     entries go; the typed amounts stay, exactly as newTaxData does. */
  useEffect(() => {
    setExtraTaxes([]);
    setExtraAdditional([]);
  }, [data]);

  const snack = (text, severity) =>
    setSnack?.((prev) => ({ ...prev, open: true, text, severity }));

  const taxCatalogue = [...rows.taxCatalogue, ...extraTaxes];
  const additionalCatalogue = [...rows.additionalCatalogue, ...extraAdditional];

  const editing = mode !== "idle";
  const isAdd = mode === "add";
  // With no markup the pre-markup row is a copy of the billed one, so the
  // column would be a duplicate of Billed. Shipment 972 is exactly that case.
  const hasQuote =
    (data?.quoteCharges || []).length > 0 && num(data?.markup) > 0;
  const compare = showQuote && hasQuote && !editing;

  // In edit mode every known line is offered, whether or not it currently
  // carries a value — otherwise a first-class column like Signature has no
  // input and the only way to charge it is to invent a named line.
  const editableTaxes = taxCatalogue;
  const editableAdditional = additionalCatalogue;

  /**
   * What the "Billed" column means depends on the mode, because the two
   * endpoints act on different things:
   *   ADD    — the line's accumulated amount across every revision, i.e. what
   *            it is billed at now. "Residential is at 20, I'm adding 5."
   *   MODIFY — the value held in the accumulated add-state that this save
   *            REPLACES, which is what the form is seeded from.
   */
  const currentFor = (kind, key) => {
    if (isAdd) {
      if (kind === "fixed") return rows.currentByKey?.[key];
      if (kind === "tax") return rows.currentTaxes?.[key];
      return rows.currentNamed?.[key];
    }
    const acc = rows.accumulated || {};
    if (kind === "fixed") return acc[key];
    // kept separate: a name present as both a tax and an accessorial must not
    // show the sum on both rows
    if (kind === "tax")
      return (acc.tax_charges || []).find((t) => t?.NameEn === key)?.Charge ?? null;
    return (
      (acc.additional_charges || []).find((c) => c?.NameEn === key)?.Charge ?? null
    );
  };

  const emptyBaseCount = [...rows.baseComponents, ...rows.baseAdditional].filter(
    (row) => !row.billed && !row.quote,
  ).length;

  /**
   * Semantic grouping. Transportation is what moves the freight, accessorials
   * are what the carrier adds for handling it, taxes sit on top. Named
   * additional charges are accessorials by definition — they are the escape
   * hatch for an accessorial without a dedicated column.
   */
  const groups = useMemo(
    () => [
      {
        key: "transport",
        title: t("invoice.transportation"),
        // slice fixedLinesFor rather than re-listing the constants: that
        // function is the single guarantee that no new key reaches the sparse
        // Add payload, and it only holds if the edit render reads from it.
        lines: lines.filter((l) => TRANSPORT_KEYS.has(l.key)),
        named: [],
      },
      {
        key: "accessorials",
        title: t("invoice.accessorials"),
        lines: lines.filter((l) => !TRANSPORT_KEYS.has(l.key)),
        named: editableAdditional,
      },
    ],
    // editableAdditional is derived from rows + local extras; both are covered
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, editableAdditional, t],
  );

  /** One charge line, in whichever mode is active. Returns null when idle and
   *  the line is empty, so the summary stays readable. */
  const renderLine = (key, label, kind) => {
    const baseRow =
      kind === "fixed"
        ? rows.baseComponents.find((r) => r.key === key)
        : rows.baseAdditional.find((r) => r.key === key);

    const billed = editing ? currentFor(kind, key) : baseRow?.billed;
    const quote = baseRow?.quote ?? null;

    if (!editing && !showEmpty && !billed && !quote) return null;

    const isNamed = kind === "named";
    return (
      <Row ctx={ctx}
        key={key}
        label={label}
        badge={editing && isNamed && !isAdd ? t("invoice.locked") : undefined}
        badgeTone={editing && isNamed && !isAdd ? "warning" : undefined}
        hint={
          editing && isNamed && !isAdd ? t("invoice.useAddChargeToChange") : undefined
        }
        quote={quote}
        billed={billed}
        editKey={editing ? (isNamed && !isAdd ? null : key) : null}
        edits={isNamed ? additionalEdits : fixedEdits}
        setEdits={isNamed ? setAdditionalEdits : setFixedEdits}
      />
    );
  };

  const { delta, projected } = previewDelta({
    mode,
    rows,
    fixedEdits,
    taxEdits,
    additionalEdits,
  });

  const reset = () => {
    setMode("idle");
    setFixedEdits({});
    setTaxEdits({});
    setAdditionalEdits({});
    setFiles(null);
    setExtraTaxes([]);
    setExtraAdditional([]);
  };

  const startAdd = () => {
    reset();
    setMode("add");
  };

  const startModify = () => {
    reset();
    setMode("modify");
    // Modify replaces the ACCUMULATED state the endpoint holds — the last
    // revision's total_updated_charges — not the flattened current view.
    // Seeding from anything else would send different numbers than production.
    const acc = rows.accumulated || {};
    const seed = {};
    lines.forEach(({ key }) => {
      if (acc[key] != null) seed[key] = acc[key];
    });
    if (acc.sub_total != null) seed.sub_total = acc.sub_total;
    if (acc.total != null) seed.total = acc.total;
    // Production seeds the note from the charge being replaced
    // (newChargeData = total_updated_charges, BillingAdmin.jsx:472-475), so
    // leaving it untouched re-sends the server's value VERBATIM — a null goes
    // out as "null", an absent key as the "" default. Copy the key only if it
    // exists; seeding undefined would override that default with "undefined".
    if (typeof acc === "object" && "description" in acc)
      seed.description = acc.description;
    setFixedEdits(seed);

    // Production's seed, bugs included: accessorials spread over taxes, and an
    // empty seed when either array is missing. See seedModifyTaxEdits.
    setTaxEdits(seedModifyTaxEdits(rows.accumulated));
  };

  const applyResult = (result) => {
    setData((prev) => ({
      ...prev,
      Total: result?.total,
      shipment_charges: [...(prev?.shipment_charges ?? []), result],
    }));
    setShipments?.((prev) => ({
      ...prev,
      data: prev?.data?.map((shipment) =>
        shipment.id === data.id
          ? {
              ...shipment,
              Total: result?.total,
              shipment_charges: [...(shipment?.shipment_charges ?? []), result],
            }
          : shipment,
      ),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const response = isAdd
      ? await submitAddCharge({
          shipmentId: data?.id,
          fixedEdits,
          taxEdits,
          additionalEdits,
          taxCatalogue,
          additionalCatalogue,
          files,
        })
      : await submitModifyCharge({
          chargeRowId: rows.latest?.id,
          totalUpdatedCharges: rows.accumulated,
          fixedEdits,
          taxEdits,
          taxCatalogue,
          files,
        });
    // Production drops the picked files on BOTH outcomes, before it looks at
    // the response (BillingAdmin.jsx:211, 288). After a failure the editor
    // stays open with the amounts intact but no attachment, so a retry posts
    // no file[] unless the admin picks again. Matches Classic, see COVERAGE.md.
    setFiles(null);
    setSaving(false);

    if (response?.error) {
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }
    // Tell the derivation this row holds a Modify's absolutes. Display only:
    // it tags nothing, so setData / setShipments get production's exact row.
    if (!isAdd) markModifyResult(response?.data?.result);
    applyResult(response?.data?.result);
    snack(
      isAdd ? t("invoice.chargeAddedSuccessfully") : t("invoice.invoiceUpdatedSuccessfully"),
      "success",
    );
    reset();
  };

  const cols = 2 + (compare ? 1 : 0) + (editing ? 1 : 0);
  const ctx = useMemo(
    () => ({ compare, editing, isAdd, cols }),
    [compare, editing, isAdd, cols],
  );

  return (
    <>
      <ImagesModal setOpen={setFullview} open={fullview} />
      <NewNamedChargeModal
        kind={namedModal}
        onClose={() => setNamedModal(null)}
        onAdd={({ name, amount }) => {
          // A second click of a double-click can land while the modal fades
          // out, after namedModal is already null — it would add the line
          // again, as an accessorial. Production's modal unmounts at once, so
          // one click adds one line.
          if (!namedModal) return;
          const isTax = namedModal === "tax";
          // {Id, Charge, NameEn} — production's insertion order, so the
          // multipart field order matches too.
          const entry = {
            Id: nextChargeId(isTax ? taxCatalogue : additionalCatalogue),
            Charge: amount,
            NameEn: name,
          };
          if (isTax) {
            setExtraTaxes((prev) => [...prev, entry]);
            setTaxEdits((prev) => ({ ...prev, [name]: amount }));
          } else {
            setExtraAdditional((prev) => [...prev, entry]);
            setAdditionalEdits((prev) => ({ ...prev, [name]: amount }));
          }
          setNamedModal(null);
        }}
      />

      <Panel
        title={t("invoice.chargeLedger")}
        chip={t("invoice.revisionCount", {
          count: rows.effectiveRevisions.length,
        })}
        chipTone="info"
        hint={
          editing
            ? undefined
            : t("invoice.ledgerHint")
        }
        actions={
          editing ? (
            <button
              onClick={reset}
              disabled={saving}
              className="flex items-center gap-1 rounded-[5px] border border-ui-border bg-ui-surface px-[9px] py-1 font-manrope text-[11.5px] font-semibold text-ui-text-muted hover:bg-ui-surface-sunken"
            >
              <MdClose className="h-3.5 w-3.5" />
              {t("common:actions.cancel")}
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              {emptyBaseCount > 0 && (
                <button
                  onClick={() => setShowEmpty((v) => !v)}
                  className="rounded-[5px] border border-ui-border bg-ui-surface px-[9px] py-1 font-manrope text-[11.5px] font-semibold text-ui-text-muted hover:border-ui-border-strong"
                >
                  {showEmpty
                    ? t("invoice.hideEmptyLines")
                    : t("invoice.showEmptyLines", { count: emptyBaseCount })}
                </button>
              )}
              {hasQuote && (
                <button
                  onClick={() => setShowQuote((v) => !v)}
                  className={`rounded-[5px] border px-[9px] py-1 font-manrope text-[11.5px] font-semibold ${
                    showQuote
                      ? "border-brand bg-state-info-bg text-state-info-text"
                      : "border-ui-border bg-ui-surface text-ui-text-muted"
                  }`}
                >
                  {showQuote ? "✓ " : ""}{t("invoice.showPreMarkup")}
                </button>
              )}
              <button
                onClick={startModify}
                /* Classic hides Save unless the latest row has a truthy id
                   (chargeDescription, BillingAdmin.jsx:469-471, :605), so it
                   can never POST charges/update/undefined. Neither can this. */
                disabled={!rows.latest?.id}
                className="flex items-center gap-1 rounded-[5px] border border-brand bg-brand px-[9px] py-1 font-manrope text-[11.5px] font-semibold text-brand-contrast hover:bg-brand-hover disabled:opacity-50"
              >
                <MdEdit className="h-3.5 w-3.5" />
                {t("invoice.modifyInvoice")}
              </button>
              <button
                onClick={startAdd}
                className="flex items-center gap-1 rounded-[5px] border border-state-success-solid bg-state-success-solid px-[9px] py-1 font-manrope text-[11.5px] font-semibold text-brand-contrast hover:bg-state-success-solid-hover"
              >
                <MdAdd className="h-3.5 w-3.5" />
                {t("invoice.addCharge")}
              </button>
            </div>
          )
        }
      >
        {editing && (
          <div
            className={`flex flex-wrap items-center gap-2 border-b px-2.5 py-1.5 ${
              isAdd
                ? "border-state-success-border bg-state-success-bg"
                : "border-state-info-border bg-state-info-bg"
            }`}
          >
            <span
              className={`rounded px-[7px] py-0.5 font-manrope text-[10.5px] font-bold uppercase tracking-[0.04em] ${
                isAdd
                  ? "bg-state-success-solid text-brand-contrast"
                  : "bg-brand text-brand-contrast"
              }`}
            >
              {isAdd ? t("invoice.addingACharge") : t("invoice.replacingTheStoredFigures")}
            </span>
            <span
              className={`font-manrope text-[11px] font-medium ${
                isAdd ? "text-state-success-text" : "text-state-info-text"
              }`}
            >
              {isAdd ? (
                <Lead label={t("invoice.addLead")}>
                  {t("invoice.addLeadText")}
                </Lead>
              ) : (
                <Lead label={t("invoice.modifyLead")}>
                  {t("invoice.modifyLeadText")}
                </Lead>
              )}
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[540px] border-collapse">
            <thead>
              <tr className="border-b border-ui-border-strong bg-ui-surface-sunken">
                <Th>{t("invoice.chargeLine")}</Th>
                {compare && <Th align="right">{t("invoice.preMarkup")}</Th>}
                <Th align="right" className="!text-state-info-text">
                  {t("invoice.billed")}
                </Th>
                {editing && (
                  <Th align="right">{isAdd ? t("common:actions.add") : t("invoice.replaceWith")}</Th>
                )}
              </tr>
            </thead>
            <tbody>
              {/* One continuous list. A post-booking charge is still a charge,
                  so it sits with the others carrying its provenance instead of
                  in a separate block at the foot. The chain reconciles:
                  charges + service = subtotal, + tax = total. */}
              {editing ? (
                <>
                  <Section ctx={ctx} right={isAdd ? t("invoice.billedNow") : t("invoice.stored")}>
                    {t("invoice.chargeLines")}
                  </Section>
                  {groups.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.lines.map(({ key, label }) =>
                        renderLine(
                          key,
                          t(`invoice.lines.${key}`, { defaultValue: label }),
                          "fixed",
                        ),
                      )}
                      {group.named.map((charge) =>
                        renderLine(charge.NameEn, charge.NameEn, "named"),
                      )}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <>
                  <Section ctx={ctx} right={t("invoice.atBookingUnlessNoted")}>{t("invoice.charges")}</Section>
                  {/* The original design's empty state: a shipment can exist
                      before anything has been billed against it. Without this
                      the ledger renders an empty list under a 0 subtotal and
                      looks broken rather than empty. */}
                  {rows.revisions.length === 0 && (
                    <tr className="border-b border-ui-border-subtle">
                      <td
                        colSpan={cols}
                        className="px-2.5 py-5 text-center font-manrope text-[11.5px] font-medium leading-relaxed text-ui-text-muted [text-wrap:pretty]"
                      >
                        <Lead label={t("invoice.nothingBilledYet")}>
                          {t("invoice.noChargeYet")}
                        </Lead>
                      </td>
                    </tr>
                  )}
                  {rows.flatCharges
                    .filter((line) => showEmpty || line.amount || line.origins.length)
                    .map((line) => (
                      <ChargeRow ctx={ctx} key={line.key} line={line} />
                    ))}
                </>
              )}

              {/* Service charge — a server field, in no payload anywhere */}
              {rows.serviceCharge > 0 && !editing && (
                <Row ctx={ctx}
                  label={t("invoice.serviceCharge")}
                  badge={rows.markup ? t("invoice.markupRate", { rate: rows.markup }) : t("invoice.markupBadge")}
                  badgeTone="purple"
                  /* No basis is stated. On 1081 the markup is exactly 25% of
                     the pre-markup subtotal; on production 22056 it is not
                     (13.32 x 1.55 = 20.65, not the 31.10 subtotal). It is a
                     stored server field — display it, do not explain it. */
                  hint={undefined}
                  tone="markup"
                  quote={null}
                  billed={rows.serviceCharge}
                />
              )}

              {/* Referral markup that isn't a clean on-top amount — a note,
                  not a line, so nothing is counted twice. "Included above"
                  only when proven; otherwise neutral, since payment-time
                  carriers store raw rows that don't add up. Display only. */}
              {referral.amount > 0 && !referralOnTop && !editing && (
                <tr>
                  <td
                    colSpan={cols}
                    className="border-b border-ui-border-subtle bg-ledger-service px-[9px] py-1.5 font-manrope text-[10.5px] font-medium leading-[1.45] text-state-purple-text"
                  >
                    <Lead
                      label={
                        referral.rate
                          ? t("invoice.referralMarkupWithRate", {
                              amount: money(referral.amount),
                              rate: referral.rate,
                            })
                          : t("invoice.referralMarkupAmount", {
                              amount: money(referral.amount),
                            })
                      }
                    >
                      {referralInside
                        ? t("invoice.referralIncluded")
                        : t("invoice.referralInTotal")}{" "}
                      · {referral.detail}
                    </Lead>
                  </td>
                </tr>
              )}

              <Row ctx={ctx}
                label={t("invoice.subtotal")}
                bold
                quote={editing ? null : rows.quoteSubTotal}
                billed={
                  editing ? rows.accumulated?.sub_total : rows.flatSubtotal
                }
                editKey={editing ? "sub_total" : null}
                edits={fixedEdits}
                setEdits={setFixedEdits}
              />

              <Section ctx={ctx}>{t("invoice.taxes")}</Section>
              {editing
                ? editableTaxes.map((tax) => (
                    <Row ctx={ctx}
                      key={tax.NameEn}
                      label={tax.NameEn}
                      billed={currentFor("tax", tax.NameEn)}
                      editKey={tax.NameEn}
                      edits={taxEdits}
                      setEdits={setTaxEdits}
                    />
                  ))
                : rows.flatTaxes.map((tax) => (
                    <ChargeRow ctx={ctx} key={tax.key} line={tax} />
                  ))}
              {!editing && rows.flatTaxes.length === 0 && (
                <tr className="border-b border-ui-border-subtle">
                  <td
                    colSpan={cols}
                    className="px-[9px] py-1 font-manrope text-[11px] font-medium text-ui-text-subtle"
                  >
                    {t("invoice.noTaxOnThisInvoice")}
                  </td>
                </tr>
              )}

              {editing && (
                <tr>
                  <td colSpan={cols} className="px-[9px] py-1.5">
                    {isAdd && (
                      <button
                        onClick={() => setNamedModal("tax")}
                        className="rounded border border-dashed border-ui-border-strong px-[9px] py-1 font-manrope text-[11px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand"
                      >
                        {t("invoice.newTaxLine")}
                      </button>
                    )}
                    {isAdd && (
                      <button
                        onClick={() => setNamedModal("additional")}
                        className="ml-1.5 rounded border border-dashed border-ui-border-strong px-2 py-1 font-manrope text-[11px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand"
                      >
                        {t("invoice.newAccessorial")}
                      </button>
                    )}
                    <span className="ml-2 font-manrope text-[10px] font-medium text-ui-text-muted">
                      {t("invoice.onlyForNamesNotAlready")}
                    </span>
                  </td>
                </tr>
              )}

              {/* Referral markup added to Total at payment, after tax and
                  outside every charge row. Display only. */}
              {referralOnTop && !editing && (
                <>
                  <Section ctx={ctx} right={t("invoice.addedAtPayment")}>
                    {t("invoice.referralSection")}
                  </Section>
                  <Row ctx={ctx}
                    label={t("invoice.referralMarkup")}
                    badge={
                      referral.rate ? t("invoice.rateReferral", { rate: referral.rate }) : t("invoice.referralBadge")
                    }
                    badgeTone="purple"
                    hint={referral.detail}
                    tone="markup"
                    quote={null}
                    billed={referral.amount}
                  />
                </>
              )}

              {/* ── Total ────────────────────────────────────────────── */}
              <tr className="bg-ui-inverted">
                <td className="px-[9px] py-1.5 font-jakarta text-[11.5px] font-extrabold text-brand-contrast">
                  {t("invoice.total")}
                </td>
                {compare && (
                  <td className="px-[9px] py-1.5 text-right font-manrope text-[12.5px] font-extrabold tabular-nums text-brand-contrast">
                    {money(rows.quoteTotal)}
                  </td>
                )}
                <td className="bg-brand px-[9px] py-1.5 text-right font-manrope text-[12.5px] font-extrabold tabular-nums text-brand-contrast">
                  {money(rows.total)}
                </td>
                {editing && (
                  <td className="px-2 py-1 text-right">
                    {isAdd ? (
                      <span className="font-manrope text-[10px] text-brand-contrast opacity-70">
                        {t("invoice.server")}
                      </span>
                    ) : (
                      <Input
                        name="total"
                        isAdd={isAdd}
                        /* `?? 0` matches Classic BillingTable.jsx:410. With no
                           stored total the box shows 0, so typing 0 changes
                           nothing and sends no `total` key — as in Classic. */
                        value={fixedEdits.total ?? 0}
                        onChange={(value) =>
                          setFixedEdits((prev) => ({ ...prev, total: value }))
                        }
                      />
                    )}
                  </td>
                )}
              </tr>

              {/* The pre-markup row is the one figure on this screen that can
                  contradict itself: on production 22056 it stores a total of
                  20.45 over lines that add to 14.29. Say so rather than print
                  a column that does not add up — an admin reading a 20.45
                  they cannot trace back is worse than an admin told it is the
                  stored number and that nothing billed depends on it. */}
              {compare &&
                rows.quoteColumnSum &&
                rows.quoteTotal &&
                Math.abs(rows.quoteColumnSum - rows.quoteTotal) > 0.01 && (
                  <tr>
                    <td
                      colSpan={cols}
                      className="border-b border-ui-border-subtle bg-state-warning-bg px-[9px] py-1.5 font-manrope text-[10.5px] font-medium leading-[1.45] text-state-warning-text"
                    >
                      <AlertGlyph />
                      <Trans
                        t={t}
                        i18nKey="invoice.preMarkupMismatch"
                        values={{
                          stored: money(rows.quoteTotal),
                          sum: money(rows.quoteColumnSum),
                        }}
                        components={{ b: <span className="font-bold tabular-nums" /> }}
                      />
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>

        {editing && (
          <div className="flex flex-col gap-2 border-t border-ui-border px-2.5 py-2">
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                <Eyebrow>{t("invoice.reasonForThisChange")}</Eyebrow>
                {/* Writes into the same bag as the amounts, as production's
                    textarea writes newChargeData.description
                    (BillingAdmin.jsx:551-557, 613-619). */}
                <textarea
                  name="description"
                  value={fixedEdits.description ?? ""}
                  onChange={(e) => {
                    const { value } = e.target;
                    setFixedEdits((prev) => ({ ...prev, description: value }));
                  }}
                  placeholder={t("invoice.chargeNotePlaceholder")}
                  className="min-h-[52px] w-full resize-y rounded-[5px] border border-ui-border-strong bg-ui-surface px-2.5 py-2 font-manrope text-[12px] font-medium leading-[1.55] text-ui-text"
                />
              </div>
              <div className="flex max-w-[240px] flex-col gap-1">
                <Eyebrow>{t("invoice.attachment")}</Eyebrow>
                <label className="flex cursor-pointer items-center gap-1.5 rounded-[5px] border border-dashed border-ui-border-strong bg-ui-surface px-3 py-[9px] font-manrope text-[11.5px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand">
                  <input
                    type="file"
                    multiple
                    hidden
                    /* Replace, not append — production does
                       setSelectedAttachment(Array.from(e.target.files)), so a
                       re-pick discards the first choice. Appending sent a
                       superseded document to the customer's invoice. */
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  />
                  <MdAttachFile className="h-4 w-4" />
                  {pending.length ? t("invoice.replaceAttachment") : t("invoice.uploadAttachment")}
                </label>

                {/* What is actually about to be sent. A count alone is a weak
                    confirmation for something that lands on a customer-visible
                    invoice. */}
                {pending.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {pending.map(({ file, isImage, url }, index) => (
                      <AttachmentChip
                        key={`${file.name}-${index}`}
                        name={file.name}
                        meta={fileSize(file.size)}
                        isImage={isImage}
                        thumbUrl={url}
                        onPreview={() =>
                          isImage && url
                            ? setFullview(url)
                            : window.open(URL.createObjectURL(file), "_blank")
                        }
                        onRemove={() =>
                          setFiles(files.filter((_, i) => i !== index))
                        }
                      />
                    ))}
                  </div>
                )}
                {/* The stored tag is meta="admin", which reads as internal and
                    is not — NotesUser.jsx:86 renders exactly that tag on the
                    customer's invoice. Worth saying out loud at the point an
                    admin picks a file. */}
                <span className="font-manrope text-[10.5px] font-medium leading-snug text-state-warning-text">
                  <Lead label={t("invoice.customerSeesThis")}>
                    {t("invoice.attachmentsAppear")}
                  </Lead>
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {projected != null && (
                <span className="font-manrope text-[10.5px] font-medium text-ui-text-muted">
                  {isAdd ? t("invoice.totalBecomes") : t("invoice.newTotal")}{" "}
                  <span className="font-bold tabular-nums text-ui-text">
                    {fixedDecimals(projected)}
                  </span>
                  {delta != null && (
                    <span
                      className={`ml-1 font-bold tabular-nums ${
                        delta > 0
                          ? "text-state-success-text"
                          : "text-state-danger-text"
                      }`}
                    >
                      ({signed(delta)})
                    </span>
                  )}
                  <span className="ml-1 text-ui-text-subtle">
                    {t("invoice.previewOnly")}
                  </span>
                </span>
              )}
              <div className="flex-1" />
              <button
                onClick={reset}
                disabled={saving}
                className="rounded-[5px] border border-ui-border bg-ui-surface px-3 py-1.5 font-manrope text-[11px] font-semibold text-ui-text hover:bg-ui-surface-sunken"
              >
                {t("common:actions.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center gap-1.5 rounded-[5px] px-4 py-1.5 font-manrope text-[11px] font-semibold text-brand-contrast disabled:opacity-60 ${
                  isAdd
                    ? "border border-state-success-solid bg-state-success-solid hover:bg-state-success-solid-hover"
                    : "border border-brand bg-brand hover:bg-brand-hover"
                }`}
              >
                {saving ? (
                  <CircularLoader size={14} color="grey" />
                ) : isAdd ? (
                  t("invoice.addCharge")
                ) : (
                  t("invoice.replaceAmounts")
                )}
              </button>
            </div>
          </div>
        )}
      </Panel>
    </>
  );
};

export default ChargeLedger;
